import { type KeyboardEventHandler, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { type CorrectionsDialogViewModel } from './CorrectionsDialogViewModel'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { type QuestionListViewModel } from '@/features/jobs/questions/QuestionListViewModel'
import { type QuestionEditorViewModel } from '@/features/jobs/questions/QuestionEditorViewModel'
import { AnswerEditorRendererContainer } from '@/features/jobs/questions/components/AnswerEditorRendererContainer'
import { QuestionEditorCompactItemContainer } from '@/features/jobs/questions/components/QuestionEditorCompactItem/QuestionEditorCompactItemContainer'
import { CorrectionsDialog } from '@/features/jobs/details/components/CorrectionsDialog'

export interface CorrectionsDialogContainerProps {
  vm: CorrectionsDialogViewModel
}

export const CorrectionsDialogContainer = observer(
  function CorrectionsDialogContainer({ vm }: CorrectionsDialogContainerProps) {
    const onEnterDown = useCallback(() => void vm.save(), [vm])
    return (
      <CorrectionsDialog
        showing={vm.modal.showing}
        onRequestClose={vm.tasks.pending ? undefined : vm.modal.close}
        jobName={vm.job.name}
        reasonInput={
          <TextInputContainer
            model={vm}
            field="reason"
            label="Reason"
            isDisabled={vm.tasks.pending}
            placeholder="e.g. the answers were wrong"
            help="Specify why you are making this change."
            data-test="reason-text-input"
          />
        }
        isLoadingInitial={vm.isLoadingInitial}
        questions={
          <QuestionsEditorContainer
            isDisabled={vm.save.pending}
            vm={vm.questions}
            onEnterDown={onEnterDown}
          />
        }
        saveError={vm.saveError}
        onSaveErrorClose={vm.save.reset}
        isPending={vm.tasks.pending}
        onCancel={vm.modal.close}
        onSave={vm.save}
        saveTitle={vm.saveTitle}
        isSaveDisabled={!vm.canSaveResponses}
      />
    )
  },
)

interface QuestionsEditorContainerProps {
  vm: QuestionListViewModel
  isDisabled: boolean
  onEnterDown: KeyboardEventHandler
}

const QuestionsEditorContainer = observer(function QuestionsEditorContainer({
  vm,
  isDisabled,
  onEnterDown,
}: QuestionsEditorContainerProps) {
  return (
    <>
      {vm.questionVms.map((q) => (
        <QuestionItemContainer
          key={q.id}
          vm={q}
          isDisabled={isDisabled}
          onEnterDown={onEnterDown}
        />
      ))}
    </>
  )
})

interface QuestionItemContainerProps {
  vm: QuestionEditorViewModel
  isDisabled: boolean
  onEnterDown: KeyboardEventHandler
}

const QuestionItemContainer = observer(function QuestionItemContainer({
  vm,
  isDisabled,
  onEnterDown,
}: QuestionItemContainerProps) {
  return (
    <QuestionEditorCompactItemContainer vm={vm}>
      <AnswerEditorRendererContainer
        questionId={vm.id}
        editorVm={vm.editor}
        isDisabled={isDisabled}
        onEnterDown={onEnterDown}
      />
    </QuestionEditorCompactItemContainer>
  )
})
